import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import "./registerServiceWorker";
import BootstrapVue from "bootstrap-vue";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-vue/dist/bootstrap-vue.css";
import axios from "axios";

import VueNotify from 'vue-notifyjs';
import 'vue-notifyjs/themes/now-ui.css';
import NowUIKit from './plugins/now-ui-kit';
import VueGlide from "vue-glide-js";
import 'vue-glide-js/dist/vue-glide.css'

import VueSweetalert2 from 'vue-sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';

import AuthenticationService from '@/services/AuthenticationService';
import { clearStoredToken, getStoredToken, storeToken } from '@/services/Session';

Vue.config.productionTip = false;

Vue.use(VueGlide);
Vue.use(VueNotify);
Vue.use(NowUIKit);
Vue.use(BootstrapVue);
Vue.use(VueSweetalert2);

function emptyPortal() {
  return {
    user: {
      title: ""
    },
    aboutUrl: ""
  };
}

function emptyAccount() {
  return {
    tenant: {
      tenantName: ""
    }
  };
}

async function restoreSessionFromStorage() {
  const token = getStoredToken();
  if (!token) {
    return {
      isAuthenticated: false,
      token: "",
      portal: emptyPortal(),
      account: emptyAccount(),
    };
  }

  axios.defaults.headers.common['x-access-token'] = token;

  try {
    const result = await AuthenticationService.authorize();
    if (result.status === 200 && result.data) {
      const renewed = result.data['x-access-token'] || token;
      storeToken(renewed);
      axios.defaults.headers.common['x-access-token'] = renewed;
      return {
        isAuthenticated: true,
        token: renewed,
        portal: result.data.portal || emptyPortal(),
        account: result.data.account || emptyAccount(),
      };
    }
  } catch (err) {
    console.warn('Session restore failed; clearing stored token.', err && err.message);
  }

  clearStoredToken();
  axios.defaults.headers.common['x-access-token'] = null;
  return {
    isAuthenticated: false,
    token: "",
    portal: emptyPortal(),
    account: emptyAccount(),
  };
}

async function bootstrap() {
  const session = await restoreSessionFromStorage();

  // this data element is $root
  new Vue({
    router,
    render: h => h(App),
    data: {
      appTitle: "mySchool Cafe",
      appVersion: process.env.VUE_APP_VERSION,
      isAuthenticated: session.isAuthenticated,
      domain: "",
      portal: session.portal,
      account: session.account,
      token: session.token,
    },
    mounted() {
      console.log("process.env", process.env);
    },
    methods: {
      isUserAuthenticated() {
        return this.isAuthenticated;
      },
      alias() {
        if (this.portal.user.title === "") {
          this.portal.user.title = "User";
        }
        return this.portal.user.title;
      },
      removePortalData() {
        axios.defaults.headers.common['x-access-token'] = null;
        clearStoredToken();
        this.portal = emptyPortal();
      },
      async confirm(title, icon = "question") {
        let result = await this.$swal.fire({
          icon: 'question',
          title: title,
          showCancelButton: true,
          allowEscapeKey: true,
          allowEnterKey: true
        });

        if (result.dismiss) {
          return false;
        }

        return true;
      },
      async confirmYesNo(title, icon = "question") {
        let result = await this.$swal.fire({
          icon: 'question',
          title: title,
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          allowEscapeKey: true,
          allowEnterKey: true
        });

        if (result.dismiss) {
          return false;
        }

        return true;
      },
      async alert(title, icon = 'success') {
        await this.$swal.fire({
          icon: icon,
          title: title,
          allowEscapeKey: true
        });
      },
      setToken(token) {
        this.token = token || "";
        if (token) {
          storeToken(token);
          axios.defaults.headers.common['x-access-token'] = token;
        } else {
          clearStoredToken();
          axios.defaults.headers.common['x-access-token'] = null;
        }
      },
      login(userRecord) {
        this.setToken(userRecord["x-access-token"]);
        this.account = userRecord.account;
        this.portal = userRecord.portal;
        this.isAuthenticated = true;
      },
      logout() {
        this.isAuthenticated = false;
        this.account = emptyAccount();
        this.setToken("");
        this.removePortalData();
        this.$router.push({ path: '/' });
      }
    }
  }).$mount("#app");
}

bootstrap();
